import {
  AlertCircle,
  ArrowRightLeft,
  Bell,
  Bot,
  Calendar,
  Camera,
  CheckCircle,
  Clock,
  Cloud,
  Code,
  CreditCard,
  Database,
  FileText,
  Gift,
  Globe,
  Image as ImageIcon,
  Key,
  Lock,
  Mail,
  Map as MapIcon,
  MessageSquare,
  Music,
  Package,
  Phone,
  Search,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Star,
  Terminal,
  Users,
  Video,
  Workflow,
  Wrench,
  Zap,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

/**
 * Curated icon set an org tool's `icon` field may reference (a stable string
 * identifier, not a raw component) -- see relay's `app_tools.icon` column.
 * Kept short and generic since it's presented as a picker; add to this list
 * (and keep `value` stable once shipped, since it's persisted) rather than
 * exposing lucide-react's full icon set.
 */
export const TOOL_ICON_OPTIONS: ReadonlyArray<{
  value: string
  label: string
  Icon: LucideIcon
}> = [
  { value: 'wrench', label: 'Wrench', Icon: Wrench },
  { value: 'zap', label: 'Zap', Icon: Zap },
  { value: 'bot', label: 'Bot', Icon: Bot },
  { value: 'sparkles', label: 'Sparkles', Icon: Sparkles },
  { value: 'message-square', label: 'Message', Icon: MessageSquare },
  { value: 'image', label: 'Image', Icon: ImageIcon },
  { value: 'calendar', label: 'Calendar', Icon: Calendar },
  { value: 'phone', label: 'Phone', Icon: Phone },
  { value: 'arrow-right-left', label: 'Transfer', Icon: ArrowRightLeft },
  { value: 'workflow', label: 'Workflow', Icon: Workflow },
  { value: 'mail', label: 'Mail', Icon: Mail },
  { value: 'file-text', label: 'File', Icon: FileText },
  { value: 'globe', label: 'Globe', Icon: Globe },
  { value: 'database', label: 'Database', Icon: Database },
  { value: 'search', label: 'Search', Icon: Search },
  { value: 'bell', label: 'Bell', Icon: Bell },
  { value: 'clock', label: 'Clock', Icon: Clock },
  { value: 'check-circle', label: 'Check', Icon: CheckCircle },
  { value: 'alert-circle', label: 'Alert', Icon: AlertCircle },
  { value: 'star', label: 'Star', Icon: Star },
  { value: 'camera', label: 'Camera', Icon: Camera },
  { value: 'video', label: 'Video', Icon: Video },
  { value: 'map', label: 'Map', Icon: MapIcon },
  { value: 'shopping-cart', label: 'Shopping Cart', Icon: ShoppingCart },
  { value: 'credit-card', label: 'Credit Card', Icon: CreditCard },
  { value: 'users', label: 'Users', Icon: Users },
  { value: 'settings', label: 'Settings', Icon: Settings },
  { value: 'shield-check', label: 'Shield Check', Icon: ShieldCheck },
  { value: 'lock', label: 'Lock', Icon: Lock },
  { value: 'key', label: 'Key', Icon: Key },
  { value: 'cloud', label: 'Cloud', Icon: Cloud },
  { value: 'code', label: 'Code', Icon: Code },
  { value: 'terminal', label: 'Terminal', Icon: Terminal },
  { value: 'package', label: 'Package', Icon: Package },
  { value: 'gift', label: 'Gift', Icon: Gift },
  { value: 'music', label: 'Music', Icon: Music },
]

export const DEFAULT_TOOL_ICON = 'wrench'

const ICON_BY_VALUE = new Map(
  TOOL_ICON_OPTIONS.map((option) => [option.value, option.Icon])
)

/** Resolve a tool's `icon` string to its component, falling back to Wrench
 * for null/unset values or any value that predates/postdates this picker's
 * curated set (e.g. set directly via the API). */
export function getToolIcon(icon: string | null | undefined): LucideIcon {
  if (!icon) return Wrench
  return ICON_BY_VALUE.get(icon) ?? Wrench
}
